import * as React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import { getActiveTopNavId, subTabGroups, getActiveSubTab } from './navRegistry';
import { CountBadge } from '../badge';

/**
 * PageToolbar - Page toolbar (46px, hairline bottom border, DESIGN-SPEC.md §3).
 *
 * Layout: page title (Archivo 600 15px) + sub-tabs (12.5px, full toolbar
 * height so the active `inset 0 -2px 0 var(--accent)` underline sits flush
 * on the bottom hairline, matching Holdings.dc.html geometry) + at most ONE
 * right-aligned primary action (§3/§4.1). Inactive sub-tabs use `text-muted-
 * token` (bound to `--text-muted` #7d94aa dark / #63737f light — light darkened
 * for AA per #239) — NOT `text-muted-
 * foreground` (bound to `--text-secondary`, one shade too light per §3's
 * "inactive nav = --text-muted"). `bg-canvas` is bound to a real `canvas`
 * Tailwind color (`tailwind.config.js`) so the toolbar surface actually
 * renders rather than emitting no CSS. Sub-tab groups are derived from the
 * shared navRegistry so they can never drift from TopNav (CC4: active
 * sub-tab is derived from useParams()/pathname only — no route table
 * changes).
 *
 * @param {Object} props
 * @param {string} props.title - page title text (falls back to section label)
 * @param {React.ReactNode} props.primaryAction - optional single right-aligned
 *   action button (e.g. "Add holding"); render at most one per screen (§3).
 * @param {React.ReactNode} props.secondaryActions - optional outline buttons
 *   rendered to the left of primaryAction
 * @param {number} props.eventsUnreadCount - drives the Events(N) sub-tab badge
 */
export function PageToolbar({ title, primaryAction, secondaryActions, eventsUnreadCount = 0 }) {
  const location = useLocation();
  const params = useParams();
  const sectionId = getActiveTopNavId(location.pathname);
  const group = sectionId ? subTabGroups[sectionId] : null;
  const activeTab = group ? getActiveSubTab(sectionId, location.pathname, params.type) : null;

  return (
    <div
      className="sticky top-[52px] z-30 flex h-[46px] shrink-0 items-center gap-1 border-b border-hairline bg-canvas px-4 md:px-6"
      data-testid="page-toolbar"
    >
      {title && (
        <h1 className="mr-4 shrink-0 truncate font-sans text-[15px] font-semibold text-foreground">
          {title}
        </h1>
      )}

      {group && (
        <nav className="flex h-full items-center gap-[2px] overflow-x-auto text-[12.5px]" aria-label="Section tabs">
          {group.tabs.map((tab) => {
            const isActive = tab.value === activeTab;
            const badgeCount = tab.unread === 'events' ? eventsUnreadCount : 0;
            return (
              <Link
                key={tab.value}
                to={tab.url}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`page-toolbar-tab-${tab.value}`}
                className={cn(
                  'relative inline-flex h-full items-center gap-1.5 whitespace-nowrap px-3 text-muted-token transition-colors duration-[120ms] ease-out',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]',
                  isActive
                    ? 'font-semibold text-foreground shadow-[inset_0_-2px_0_var(--accent,_#3b9eff)]'
                    : 'hover:text-foreground'
                )}
              >
                {tab.label}
                {badgeCount > 0 && (
                  <CountBadge count={badgeCount} variant="destructive" size="sm" />
                )}
              </Link>
            );
          })}
        </nav>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {secondaryActions}
        {primaryAction}
      </div>
    </div>
  );
}

export default PageToolbar;
